import React, { useState, useEffect } from 'react';
import { Page } from './pages.ts';

import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import { Box, Button, IconButton, List, ListItemButton, Paper, Typography } from '@mui/material';
import MenuIcon from "@mui/icons-material/Menu";
import AudioPlayer from 'material-ui-audio-player';

function MasonryImageList({page} : Readonly<{page: Page}>) {
  return (
    <ImageList variant="masonry" cols={3} gap={8} >
        {page.images.map((item) => (
            <ImageListItem key={item.image}>
            <img
                srcSet={`${item.image}?w=248&fit=crop&auto=format&dpr=2 2x`}
                src={`${item.image}?w=248&fit=crop&auto=format`}
                alt={"" + item.id}
                loading="lazy"
            />
            </ImageListItem>
        ))}
    </ImageList>
  );
}

interface PlayerControls {
    pause: () => void,
    play: () => void,
    hide: () => void,
    unhide: () => void
}

function ControlledAudioPlayer({song, controlsCallback, hidden}: {song: any, controlsCallback: (PlayerControls) => void, hidden: boolean}) {
    const [player, setPlayer] = useState<HTMLAudioElement>();

    const getPlayer = (currentPlayer, dispatch) => {
        setPlayer(currentPlayer);
    }

    const pause = () => {
        if(player) {
            player.pause(); // TODO dispatch
        }
    }

    const play = () => {
        if(player) {
            player.currentTime = 0;
            player.play();
        }
    }

    useEffect(() => {
        controlsCallback({pause, play});
    }, [hidden, player]);

    return (<Box sx={{ ...(hidden ? {display: "none"} : {})}}><AudioPlayer volume={false} src={song} preload="true" getPlayer={getPlayer}/></Box>);
}

type ControlsMap = {
    [key: number] : PlayerControls
}

function MediaPlayer({page} :  Readonly<{page: Page}>) {
    const [track, setTrack] = useState(page.songs[0]);
    const [menuOpen, setMenuOpen] = useState(false);
    const [playerControlsMap, setPlayerControlsMap] = useState<ControlsMap>({});

    const toggleMenuOpen = () => {
        setMenuOpen(!menuOpen);
    }

    const changeSong = (newSong: any) => {
        if (newSong === track) return;
        playerControlsMap[track.id].pause();
        playerControlsMap[newSong.id].play();
        setTrack(newSong);
    }

    const setPlayerControls = (controls: PlayerControls, songId: number) => {
        const newControlsMap = {...playerControlsMap};
        newControlsMap[songId] = controls;
        setPlayerControlsMap(newControlsMap);
    }
    
    return (<Box sx={{position: "fixed", bottom: "0px", width: "100%", boxSizing: "border-box"}} padding={2}>
        {menuOpen && <Paper sx={{marginBottom: "8px", width: "max-content"}}>
            <List component="nav">
                {page.songs.map(song => <ListItemButton
                        key={song.id}
                        selected={track.id === song.id}
                        onClick={() => changeSong(song)}
                    >
                        <Typography>{song.title} - {song.artist}</Typography>
                    </ListItemButton>)}
            </List>
        </Paper>}
        <Box display="flex" alignItems="center">
            <Button
                variant='contained' sx={{color: "rgba(0,0,0,0.87)", backgroundColor: "white", marginRight: "16px", minWidth: "0px", padding: "16px"}}
                onClick={toggleMenuOpen}
            >
                <MenuIcon />
            </Button>
            <Box flex="1">
                {page.songs.map( song => <ControlledAudioPlayer
                    key={song.id}
                    song={song.song}
                    controlsCallback={(controls) => setPlayerControls(controls, song.id)}
                    hidden={track.id !== song.id}
                />)}
            </Box>
        </Box>
    </Box>);
}

export default function AlbumPage({page} : Readonly<{page: Page}>) {
    return (<>
        <Box marginTop={2}><Typography variant='h3' textAlign='center'>{page.title}</Typography></Box>
        
        <Box display="flex" flexDirection="column" alignItems="center">
            <Box maxWidth={800} >
                <MasonryImageList page={page}/>
            </Box>
        </Box>
        <MediaPlayer page={page}/>
    </>);
}